import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Read-only AGENCY / LOCATION panel — the Hi-Fi handoff's `8a/echo` and `8b/echo`.
 *
 * Presented as destination information, NOT a disabled input. The handoff is explicit:
 * "Read-only is not disabled … no outline, no chevron, no grey placeholder — so nothing reads as
 * broken" (FR-19). Hence a left-rule panel with plain type rather than a greyed-out Select.
 *
 * Shared by the invite modal (inside the First appointment section, 8a) and the edit modal (8b),
 * because the two frames draw the same panel and two copies would drift.
 *
 * Values fall back to "Not yet selected" rather than rendering empty, so the panel always states
 * something — on 8a the coordinator has not chosen yet, and a blank line reads as a defect.
 *
 * Drawn as a CARD — filled, rounded, left-spined — because both frames nest it inside the
 * appointment section's own tinted panel. With only the spine it had no edges of its own, so
 * sharing the section's background made it read as loose text that happened to sit at the bottom
 * rather than as the section's closing statement. `surface.light2` is a touch bluer than the
 * section's `panel.main`, which is what separates the two without a border.
 */

interface Props {
  agency?: string;
  location?: string;
  /** Trailing note. 8b carries "Agency and location are unchanged."; 8a carries none. */
  note?: string;
}

const AllocationEcho: React.FC<Props> = ({ agency, location, note }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1,
      backgroundColor: "surface.light2",
      borderLeft: "4px solid",
      borderColor: "primary.main",
      // Square on the spine side so the rule reads as one continuous edge, rounded on the other
      // three to match the enclosing panel.
      borderRadius: "0 4px 4px 0",
      px: 2,
      py: 1.5,
    }}
  >
    <Box sx={{ display: "flex", gap: 6 }}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          AGENCY
        </Typography>
        {/* The agency is the settled answer, so it takes the near-black ink and bold weight. */}
        <Typography
          variant="body1"
          sx={{ fontWeight: 700, color: "panel.dark2" }}
        >
          {agency || "Not yet selected"}
        </Typography>
      </Box>
      <Box>
        <Typography variant="overline" color="text.secondary">
          LOCATION
        </Typography>
        {/* Secondary ink — quieter than the agency beside it. */}
        <Typography variant="body1" sx={{ color: "panel.main2" }}>
          {location || "Not yet selected"}
        </Typography>
      </Box>
    </Box>

    {note ? (
      <Typography variant="body2" color="text.secondary">
        {note}
      </Typography>
    ) : null}
  </Box>
);

export default AllocationEcho;
